import {
  Box,
  Link,
  Text,
  Wrap,
} from '@chakra-ui/react';
import {
  useRoadMode,
  useCyclingMode,
  useWalkingMode,
} from '@src/modules/performance/context/reports-api-hooks';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import LockYearBadge from '@src/modules/performance/components/LockYearBadge';
import LineRangeAreaChart from '@src/modules/performance/components/charts/LineRangeAreaChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Vulnerable User DSIs (per 100,000 population)',
  content: [
    {
      title: 'Motorcycles',
      description:
  <Text fontSize="inherit" mb={2}>
    The total number of deaths and serious injuries (DSI) each year in the selected network area per 100,000 of population involving motorcycles and mopeds.
  </Text>,
      source: null,
    },
    {
      title: 'Cycling',
      description:
  <Text fontSize="inherit" mb={2}>
    The total number of deaths and serious injuries (DSI) each year in the selected network area per 100,000 of population involving cyclists.
  </Text>,
      source: null,
    },
    {
      title: 'Walking',
      description:
  <Text fontSize="inherit" mb={2}>
    The total number of deaths and serious injuries (DSI) each year in the selected network area per 100,000 of population involving pedestrians.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Crash data sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/partners/data-and-tools/crash-analysis-system" isExternal>NZTA Crash Analysis System.</Link>
    {' '}
    Population sourced from Statistics New Zealand
    {' '}
    <Link href="https://www.stats.govt.nz/information-releases/subnational-population-estimates-at-30-june-2023/" isExternal>Subnational Population Estimates.</Link>
    {' '}
    Road Efficiency Group Te Ringa Maimoa has calculated these results from these data sources.
  </Text>,
    },
  ],
};

function RoadSafetyByMode() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: motorcyclesModeData, isLoading: isLoadingMotorcyclesMode } = useRoadMode();
  const { data: cyclingModeData, isLoading: isLoadingCyclingMode } = useCyclingMode();
  const { data: walkingModeData, isLoading: isLoadingWalkingMode } = useWalkingMode();
  const isLoading = isLoadingMotorcyclesMode || isLoadingCyclingMode || isLoadingWalkingMode;
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isNational) {
    chartFilters = ['Peer', 'Percentile'];
  } else if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  // Check if all lockYears are the same
  const allLockYears = [
    motorcyclesModeData?.[0]?.LockYear,
    cyclingModeData?.[0]?.LockYear,
    walkingModeData?.[0]?.LockYear,
  ];
  const primaryLockYear = allLockYears.every((year) => year === allLockYears[0]) ? allLockYears[0] : null;

  // Motorcycles Chart
  const motorcyclesYears = [...new Set(motorcyclesModeData?.map((item) => item.Year))];
  const motorcyclesData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: motorcyclesModeData?.map((item) => ({ x: item.Year, y: item.Value })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: motorcyclesModeData?.map((item) => ({ x: item.Year, y: item.PeerGroupValue })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th National Percentile',
    type: 'rangeArea',
    data: motorcyclesModeData?.map((item) => ({ x: item.Year, y: [item.LowPercentile, item.HighPercentile] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const motorcyclesChartSeries = filterChartDataByName(
    motorcyclesData,
    chartFilters,
  );

  // Cycling Chart
  const cyclingYears = [...new Set(cyclingModeData?.map((item) => item.Year))];
  const cyclingData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: cyclingModeData?.map((item) => ({ x: item.Year, y: item.Value })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: cyclingModeData?.map((item) => ({ x: item.Year, y: item.PeerGroupValue })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th National Percentile',
    type: 'rangeArea',
    data: cyclingModeData?.map((item) => ({ x: item.Year, y: [item.LowPercentile, item.HighPercentile] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const cyclingChartSeries = filterChartDataByName(
    cyclingData,
    chartFilters,
  );

  // Walking Chart
  const walkingYears = [...new Set(walkingModeData?.map((item) => item.Year))];
  const walkingData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: walkingModeData?.map((item) => ({ x: item.Year, y: item.Value })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: walkingModeData?.map((item) => ({ x: item.Year, y: item.PeerGroupValue })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th National Percentile',
    type: 'rangeArea',
    data: walkingModeData?.map((item) => ({ x: item.Year, y: [item.LowPercentile, item.HighPercentile] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const walkingChartSeries = filterChartDataByName(
    walkingData,
    chartFilters,
  );

  const markers = isRegionNationalOrStateHighways ? {
    size: [3, 0],
    hover: {
      size: 6,
    },
  } : {
    size: [3, 3, 0],
    hover: {
      size: 6,
    },
  };

  const stroke = isRegionNationalOrStateHighways ? {
    curve: 'straight',
    width: [2, 0],
  } : {
    curve: 'straight',
    width: [2, 2, 0],
  };

  return (
    <ReportSection
      title="Vulnerable User DSIs (per 100,000 population)"
      informationSource={informationSource}
      isLoading={isLoading || !motorcyclesModeData || !cyclingModeData || !walkingModeData}
      lockYear={primaryLockYear || null}
      links={[
        {
          label: 'ONF Safe Travel - Vulnerable Users',
          url: '/onf/transport-outcomes/hsp4',
          isExternal: false,
          userRoles: null,
        },
      ]}
    >
      <Wrap spacing={0}>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={motorcyclesModeData?.[0]?.LockYear}
          />
          )}
          <LineRangeAreaChart
            title="Motorcycles"
            categories={motorcyclesYears}
            series={motorcyclesChartSeries}
            width="240"
            height="280"
            stroke={stroke}
            markers={markers}
            prefix=""
            suffix=""
            decimalPoints={1}
          />
        </Box>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={cyclingModeData?.[0]?.LockYear}
          />
          )}
          <LineRangeAreaChart
            title="Cycling"
            categories={cyclingYears}
            series={cyclingChartSeries}
            width="240"
            height="280"
            stroke={stroke}
            markers={markers}
            prefix=""
            suffix=""
            decimalPoints={1}
          />
        </Box>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={walkingModeData?.[0]?.LockYear}
          />
          )}
          <LineRangeAreaChart
            title="Walking"
            categories={walkingYears}
            series={walkingChartSeries}
            width="240"
            height="280"
            stroke={stroke}
            markers={markers}
            prefix=""
            suffix=""
            decimalPoints={1}
          />
        </Box>
      </Wrap>
    </ReportSection>
  );
}

export default RoadSafetyByMode;
